import React, { useMemo, useState, useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CommonFilter from "./commonFilter";
import "../../styles/common/commonTable.scss";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";
import { createPortal } from "react-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const CommonTable = ({
  data,
  columns,
  getRowId,
  emptyText = "No data available",
  dense = false,
  className = "",
  containerClassName = "",
  onRowClick,
  isRowSelected,
  multiSelect = false,
  selectedRowIds,
  onSelectionChange,
  showToolbar = true,
  title,
  showSearch = true,
  searchPlaceholder = "Search",
  onSearchChange,
  showFilter = true,
  onFilterClick,
  toolbarLeft,
  toolbarRight,
  filterSections,
  filterValues,
  onFilterChange,
  showAppliedFilterChips = false,
  showActions = false,
  singleActionButton = false,
  onEdit,
  onDelete,
  handleEdit,
  handleDelete,
  actionLabelEdit = "Edit",
  actionLabelDelete = "Delete",
  extraActions,
  showEditAction = true,
  showDeleteAction = true,
  isRowActionable,
  setIsConfirmDeleteOpen,
  isConfirmDeleteOpen,
  headerExtras,
  maxBodyHeight = "calc(100vh - 200px)",
  fixedBodyRows = 10,
  rowHeight = 44,
  isRowSelectable,
  deleteMessage,
  removeActionLabel,
}) => {
  const [openActionsFor, setOpenActionsFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [internalSelected, setInternalSelected] = useState(() => new Set());

  const rows = Array.isArray(data) ? data : data ? [data] : [];

  const autoColumns = useMemo(() => {
    const first = rows[0] || {};
    return Object.keys(first)
      .filter((k) => typeof first[k] !== "object")
      .map((k) => ({ key: k, label: k.replace(/_/g, " ") }));
  }, [rows]);

  const cols = (columns && columns.length ? columns : autoColumns).map((c) => ({
    field: c.field ?? c.key,
    label:
      c.label ?? c.headerName ?? (c.field ?? c.key)?.replace(/_/g, " ") ?? "",
    align: c.align ?? "left",
    render: c.render,
  }));

  const finalCols = useMemo(() => {
    let out = cols;
    if (showActions) {
      out = [...out, { field: "__actions", label: "Actions", align: "right" }];
    }
    if (multiSelect) {
      out = [{ field: "__select", label: "", align: "left" }, ...out];
    }
    return out;
  }, [cols, showActions, multiSelect]);

  const toggleActions = (rowKey) => {
    setOpenActionsFor((prev) => (prev === rowKey ? null : rowKey));
  };
  const closeActions = () => setOpenActionsFor(null);

  const tableClass = `common-table${dense ? " dense" : ""}${
    className ? ` ${className}` : ""
  }`;
  const containerClass = `table-container${
    containerClassName ? ` ${containerClassName}` : ""
  }`;

  const selectedSet = useMemo(() => {
    if (Array.isArray(selectedRowIds)) return new Set(selectedRowIds);
    return internalSelected;
  }, [selectedRowIds, internalSelected]);

  const emitSelection = (nextSet) => {
    const arr = Array.from(nextSet);
    if (onSelectionChange) onSelectionChange(arr);
    if (!Array.isArray(selectedRowIds)) setInternalSelected(nextSet);
  };

  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (!multiSelect) return;
    const total = rows.filter((row, idx) =>
      typeof isRowSelectable === "function"
        ? !!isRowSelectable(row, idx)
        : true,
    ).length;
    const selectedCount = rows.reduce((acc, row, idx) => {
      const selectable =
        typeof isRowSelectable === "function"
          ? !!isRowSelectable(row, idx)
          : true;
      if (!selectable) return acc;
      const rowKey = getRowId ? getRowId(row) : (row.id ?? row._id ?? idx);
      return acc + (selectedSet.has(rowKey) ? 1 : 0);
    }, 0);
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        selectedCount > 0 && selectedCount < total;
    }
  }, [multiSelect, rows, selectedSet, getRowId]);

  const toggleRowSelect = (rowKey, row, idx) => {
    if (typeof isRowSelectable === "function" && !isRowSelectable(row, idx)) {
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(rowKey)) next.delete(rowKey);
    else next.add(rowKey);
    emitSelection(next);
  };

  const toggleAll = () => {
    const selectableRowKeys = rows
      .map((row, idx) => ({ row, idx }))
      .filter(({ row, idx }) =>
        typeof isRowSelectable === "function"
          ? !!isRowSelectable(row, idx)
          : true,
      )
      .map(({ row, idx }) =>
        getRowId ? getRowId(row) : (row.id ?? row._id ?? idx),
      );

    const selectedCount = selectableRowKeys.filter((k) =>
      selectedSet.has(k),
    ).length;
    const total = selectableRowKeys.length;
    if (selectedCount === total) {
      emitSelection(new Set());
    } else {
      const all = new Set(selectableRowKeys);
      emitSelection(all);
    }
  };

  useEffect(() => {
    if (!openActionsFor) return;
    const handler = () => setOpenActionsFor(null);
    const handleClickOutside = (event) => {
      const actionsMenu = document.querySelector(".actions-menu-fixed");
      const actionsButton = event.target.closest(".actions-btn");

      if (
        actionsMenu &&
        !actionsMenu.contains(event.target) &&
        !actionsButton
      ) {
        setOpenActionsFor(null);
      }
    };

    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    document.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openActionsFor]);

  return (
    <>
      <div className={containerClass}>
        {showToolbar && (
          <div className={`table-toolbar ${title ? "justify-sb" : ""}`}>
            {(title || toolbarLeft) && (
              <div className="toolbar-left">
                {title ? <h3 className="toolbar-title">{title}</h3> : null}
                {toolbarLeft}
              </div>
            )}
            <div className="toolbar-right">
              {showSearch && (
                <label className="toolbar-search">
                  <SearchIcon fontSize="small" className="search-icon" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    onChange={(e) =>
                      onSearchChange && onSearchChange(e.target.value)
                    }
                  />
                </label>
              )}
              {showFilter && (
                <CommonFilter
                  sections={filterSections}
                  values={filterValues || {}}
                  onMultiChange={onFilterChange}
                  className="ct-filter-trigger"
                  size="small"
                  showAppliedChips={showAppliedFilterChips}
                />
              )}
              {toolbarRight ?? headerExtras}
            </div>
          </div>
        )}
        <div
          className="table-scroll"
          style={{
            ...(typeof fixedBodyRows === "number" && fixedBodyRows > 0
              ? { minHeight: `${fixedBodyRows * rowHeight}px` }
              : {}),
            ...(maxBodyHeight
              ? { maxHeight: maxBodyHeight, height: maxBodyHeight }
              : {}),
          }}
        >
          <table className={tableClass}>
            <thead>
              <tr>
                {finalCols.map((col) => {
                  if (col.field === "__select") {
                    const selectableRowKeys = rows
                      .map((row, idx) => ({ row, idx }))
                      .filter(({ row, idx }) =>
                        typeof isRowSelectable === "function"
                          ? !!isRowSelectable(row, idx)
                          : true,
                      )
                      .map(({ row, idx }) =>
                        getRowId ? getRowId(row) : (row.id ?? row._id ?? idx),
                      );
                    const selectedCount = selectableRowKeys.filter((k) =>
                      selectedSet.has(k),
                    ).length;
                    const total = selectableRowKeys.length;
                    const allChecked = total > 0 && selectedCount === total;
                    return (
                      <th key="__select" scope="col" className="align-left">
                        <input
                          ref={headerCheckboxRef}
                          type="checkbox"
                          aria-label="Select all rows"
                          checked={allChecked}
                          style={{
                            cursor: "pointer",
                            accentColor: "var(--btn-primary-bg-color)",
                          }}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleAll();
                          }}
                          disabled={total === 0}
                        />
                      </th>
                    );
                  }
                  return (
                    <th
                      key={col.field}
                      scope="col"
                      className={`align-${col.align}`}
                    >
                      {col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    className="no-data"
                    style={{
                      height:
                        typeof maxBodyHeight === "number"
                          ? `${Math.max(0, maxBodyHeight - 50)}px`
                          : `calc(${maxBodyHeight} - 50px)`,
                    }}
                    colSpan={finalCols.length}
                  >
                    {emptyText}
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => {
                  const rowKey = getRowId
                    ? getRowId(row)
                    : (row.id ?? row._id ?? rowIndex);
                  const selected = isRowSelected
                    ? !!isRowSelected(row, rowIndex)
                    : false;
                  return (
                    <tr
                      key={rowKey}
                      className={`table-row ${
                        onRowClick ? "is-clickable" : ""
                      }`}
                      aria-selected={selected || undefined}
                      onClick={
                        onRowClick ? () => onRowClick(row, rowIndex) : undefined
                      }
                      role={onRowClick ? "button" : undefined}
                    >
                      {finalCols.map((col) => {
                        const value = row[col.field];
                        if (col.field === "__select") {
                          const selectable =
                            typeof isRowSelectable === "function"
                              ? !!isRowSelectable(row, rowIndex)
                              : true;
                          const checked = selectedSet.has(rowKey);
                          return (
                            <td
                              key={`__select-${rowKey}`}
                              className={`table-cell selection-cell align-left`}
                            >
                              <input
                                type="checkbox"
                                aria-label={`Select row ${rowIndex + 1}`}
                                checked={checked}
                                disabled={!selectable}
                                style={{
                                  cursor: selectable
                                    ? "pointer"
                                    : "not-allowed",
                                  accentColor: "var(--btn-primary-bg-color)",
                                  color: "var(--bg)",
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleRowSelect(rowKey, row, rowIndex);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                          );
                        }
                        if (col.field === "__actions") {
                          const editHandler = handleEdit || onEdit;
                          const deleteHandler = handleDelete || onDelete;
                          const extras = (() => {
                            if (typeof extraActions === "function") {
                              try {
                                const res = extraActions(row, rowKey);
                                return Array.isArray(res) ? res : [];
                              } catch (e) {
                                return [];
                              }
                            }
                            return Array.isArray(extraActions)
                              ? extraActions
                              : [];
                          })();
                          const actionable =
                            typeof isRowActionable === "function"
                              ? !!isRowActionable(row, rowKey)
                              : true;
                          if (singleActionButton) {
                            return (
                              <td
                                key={col.field}
                                className={`table-cell actions-cell align-${col.align}`}
                              >
                                <button
                                  type="button"
                                  className="table-action-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    editHandler && editHandler(rowKey, row);
                                  }}
                                ></button>
                              </td>
                            );
                          }
                          return (
                            <td
                              key={col.field}
                              className={`table-cell actions-cell align-${col.align}`}
                              data-label={col.label}
                            >
                              {actionable ? (
                                <button
                                  type="button"
                                  className="actions-btn"
                                  aria-label="Row actions"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect =
                                      e.currentTarget.getBoundingClientRect();
                                    const menuW = 128;
                                    const menuH = 84;
                                    const padding = 8;
                                    const gap = 6;
                                    let x = rect.left - menuW - gap;
                                    let y = rect.bottom + 2;
                                    if (!rect.width && !rect.height) {
                                      x = e.clientX - menuW - gap;
                                      y = e.clientY + 2;
                                    }
                                    const maxX =
                                      (window.innerWidth ||
                                        document.documentElement.clientWidth) -
                                      menuW -
                                      padding;
                                    const maxY =
                                      (window.innerHeight ||
                                        document.documentElement.clientHeight) -
                                      menuH -
                                      padding;
                                    x = Math.max(padding, Math.min(maxX, x));
                                    y = Math.max(padding, Math.min(maxY, y));
                                    setMenuPos({ x, y });
                                    toggleActions(rowKey);
                                  }}
                                >
                                  ⋮
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="actions-btn disabled"
                                  aria-label="Row actions disabled"
                                  disabled
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  ⋮
                                </button>
                              )}
                              {actionable &&
                                openActionsFor === rowKey &&
                                createPortal(
                                  <div
                                    className="actions-menu-fixed"
                                    role="menu"
                                    style={{
                                      left: `${menuPos.x}px`,
                                      top: `${menuPos.y}px`,
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {extras.map((act) => (
                                      <button
                                        type="button"
                                        key={String(act.key || act.label)}
                                        className={`actions-item${
                                          act.danger ? " danger" : ""
                                        }${
                                          act.className
                                            ? ` ${act.className}`
                                            : ""
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeActions();
                                          act.onClick &&
                                            act.onClick(row, rowKey);
                                        }}
                                      >
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          {act.icon}
                                          {act.label}
                                        </span>
                                      </button>
                                    ))}
                                    {showEditAction && (
                                      <button
                                        type="button"
                                        className="actions-item"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeActions();
                                          editHandler &&
                                            editHandler(rowKey, row);
                                        }}
                                      >
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                          {actionLabelEdit}
                                        </span>
                                      </button>
                                    )}
                                    {showDeleteAction && (
                                      <button
                                        type="button"
                                        className="actions-item danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeActions();
                                          setToDelete({ rowKey, row });
                                          setIsConfirmDeleteOpen(true);
                                        }}
                                      >
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                          {actionLabelDelete}
                                        </span>
                                      </button>
                                    )}
                                  </div>,
                                  document.body,
                                )}
                            </td>
                          );
                        }
                        const content = col.render
                          ? col.render(value, row)
                          : value;
                        let cellContent = "--";
                        let titleText = undefined;
                        if (content !== null && content !== undefined) {
                          if (React.isValidElement(content)) {
                            cellContent = content;
                          } else if (typeof content === "object") {
                            try {
                              const txt = JSON.stringify(content);
                              cellContent = (
                                <span className="cell-text" title={txt}>
                                  {txt}
                                </span>
                              );
                            } catch (e) {
                              const txt = String(content);
                              cellContent = (
                                <span className="cell-text" title={txt}>
                                  {txt}
                                </span>
                              );
                            }
                          } else {
                            if (typeof content === "string") {
                              const trimmed = content.trim();
                              const txt = trimmed.length > 0 ? content : "--";
                              titleText = txt !== "--" ? txt : undefined;
                              cellContent = (
                                <span className="cell-text" title={titleText}>
                                  {txt}
                                </span>
                              );
                            } else {
                              const txt = String(content);
                              titleText = txt;
                              cellContent = (
                                <span className="cell-text" title={titleText}>
                                  {txt}
                                </span>
                              );
                            }
                          }
                        }
                        return (
                          <td
                            key={col.field}
                            className={`table-cell align-${col.align}`}
                            data-label={col.label}
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          handleDelete={() => {
            const deleteHandler = handleDelete || onDelete;
            if (deleteHandler && toDelete) {
              deleteHandler(toDelete.rowKey, toDelete.row);
            }
            setIsConfirmDeleteOpen(false);
            setToDelete(null);
          }}
          message={
            deleteMessage ||
            "Are you sure you want to delete this item? This action cannot be undone."
          }
          confirmLabel={removeActionLabel || "Delete"}
        />
      )}
    </>
  );
};

export default CommonTable;
